import { meSelector } from '../../redux/app/selectors.js';
import { getStore } from '../redux/store/getStore.js';

/**
 * @typedef {function(PushNotification)} PushNotificationCallback
 */

export class PushNotifications {
  /**
   * The chartCheckId we are connected as.
   * @type {string|undefined}
   */
  _connectedAs = undefined;

  /**
   * Listeners for specific notification types.
   * @type {Array.<{ type:string, callback:PushNotificationCallback }>}
   */
  _listeners = [];

  /**
   * Subscriptions which receive all notifications.
   * @type {PushNotificationCallback[]}
   */
  _subscriptions = [];

  /**
   * The actual connection
   * @type {EventSource|undefined}
   */
  _eventSource;

  /**
   * @type {function()|undefined}
   */
  _unsubscribeStore = undefined;

  isConnected() {
    return this._eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Connects to the server.
   * Automatically reconnect or disconnects if the user's credentials change (they log in/out)
   */
  connect() {
    const me = meSelector(getStore().getState());

    this._eventSource = new EventSource('/weekly-check/api/push');
    this._eventSource.addEventListener('message', this._handleMessage);

    this._unsubscribeStore = getStore().subscribe(this._handleStateChange);

    this._connectedAs = me?.chartCheckId;
  }

  /**
   * Disconnects from the server.
   */
  disconnect() {
    this._eventSource?.close();
    this._eventSource = undefined;

    this._unsubscribeStore?.();

    this._connectedAs = undefined;
  }

  /**
   * Listens for a specific notification type.
   * @param {string} type
   * @param {PushNotificationCallback} callback
   */
  addListener(type, callback) {
    this._listeners.push({ type, callback });
  }

  /**
   * Removes a listener.
   * @param {string} type
   * @param {PushNotificationCallback} callback
   */
  removeListener(type, callback) {
    const index = this._listeners.findIndex(subscription =>
      subscription.type === type && subscription.callback === callback);

    if (index === -1) return;

    this._listeners.splice(index, 1);
  }

  /**
   * Subscribes to all messages.
   * @param {PushNotificationCallback} callback
   */
  subscribe(callback) {
    this._subscriptions.push(callback);
  }

  /**
   * Removes a specific subscribe() callback.
   * @param {PushNotificationCallback} callback
   */
  unsubcribe(callback) {
    const index = this._subscriptions.indexOf(callback);

    if (index === -1) return;

    this._subscriptions.splice(index, 1);
  }

  _handleStateChange = state => {
    const me = meSelector(state);

    if (this._eventSource && me?.chartCheckId !== this._connectedAs) {
      this.disconnect();
      this.connect();
    }
  };

  _handleMessage = event => {
    const data = JSON.parse(event.data);
    const { type } = data;

    const callbacks = [
      ...this._listeners.filter(s => s.type === type)
        .map(({ callback }) => callback),
      ...this._subscriptions
    ];

    for (let callback of callbacks) {
      callback(data);
    }
  };
}

export const pushNotifications = new PushNotifications();